<template>
  <div class="pb-page gv">
    <PageHeader title="Quote graphics" subtitle="Turn a published testimonial into an image for social posts, slides or your website. Only consented details are used." />

    <SkeletonList v-if="loading" :rows="3" label="Loading testimonials…" />
    <div v-else-if="error" class="pb-alert is-error" role="alert"><span>{{ error }}</span><button type="button" class="pb-button sm outline" @click="load">Retry</button></div>
    <EmptyState v-else-if="!items.length" icon="mdi-image-text" title="Publish a testimonial first"
                text="Quote graphics use published testimonials, with the details each customer allowed.">
      <router-link to="/inbox" class="pb-button">Open inbox</router-link>
    </EmptyState>

    <div v-else class="gv-grid">
      <div class="gv-controls">
        <section class="pb-card" aria-labelledby="gv-pick">
          <h2 id="gv-pick" class="pb-section-title display-font gv-h">Testimonial</h2>
          <label class="pb-sr-only" for="gv-select">Choose a published testimonial</label>
          <select id="gv-select" v-model.number="pickedId" class="pb-select">
            <option v-for="item in items" :key="item.id" :value="item.id">{{ label(item) }}</option>
          </select>
        </section>

        <section class="pb-card" aria-labelledby="gv-format">
          <h2 id="gv-format" class="pb-section-title display-font gv-h">Format</h2>
          <div class="gv-options" role="radiogroup" aria-labelledby="gv-format">
            <button v-for="(spec, key) in formats" :key="key" type="button" role="radio" :aria-checked="format === key ? 'true' : 'false'"
                    :aria-disabled="locked('format', key) ? 'true' : null" :class="['gv-option', { picked: format === key, locked: locked('format', key) }]" @click="choose('format', key)">
              <span :class="['gv-shape', key]" aria-hidden="true"></span>
              <span class="gv-o-name">{{ spec.label }}<PlanChip v-if="locked('format', key)" :plan="graphicsPlan" /></span>
              <span class="pb-muted">{{ spec.note }}</span>
            </button>
          </div>
        </section>

        <section class="pb-card" aria-labelledby="gv-template">
          <h2 id="gv-template" class="pb-section-title display-font gv-h">Template</h2>
          <div class="gv-options" role="radiogroup" aria-labelledby="gv-template">
            <button v-for="(spec, key) in templates" :key="key" type="button" role="radio" :aria-checked="template === key ? 'true' : 'false'"
                    :aria-disabled="locked('template', key) ? 'true' : null" :class="['gv-option', { picked: template === key, locked: locked('template', key) }]" @click="choose('template', key)">
              <span :class="['gv-swatch', key]" :style="key === 'bold' ? { background: brandColor } : null" aria-hidden="true"></span>
              <span class="gv-o-name">{{ spec.label }}<PlanChip v-if="locked('template', key)" :plan="graphicsPlan" /></span>
              <span class="pb-muted">{{ spec.note }}</span>
            </button>
          </div>
        </section>

        <section class="pb-card" aria-labelledby="gv-details">
          <h2 id="gv-details" class="pb-section-title display-font gv-h">Details</h2>
          <label class="pb-check"><input v-model="showName" type="checkbox" :disabled="!picked || !picked.name">Name<span v-if="picked && !picked.name" class="pb-muted">&nbsp;(not shared by the customer)</span></label>
          <label class="pb-check gv-gap"><input v-model="showRole" type="checkbox" :disabled="!role">Job title and company</label>
          <label class="pb-check gv-gap"><input v-model="showRating" type="checkbox" :disabled="!picked || !picked.rating">Star rating</label>
          <p v-if="picked && picked.source" class="pb-help gv-source">Imported, so the graphic says where it came from: “{{ picked.source }}”.</p>
        </section>
      </div>

      <section class="gv-preview" aria-labelledby="gv-prev">
        <h2 id="gv-prev" class="pb-label">Preview</h2>
        <div class="gv-canvas-wrap"><canvas ref="canvas" class="gv-canvas" role="img" :aria-label="'Quote graphic: ' + (picked ? picked.text : '')"></canvas></div>
        <div v-if="!fits" class="pb-alert" role="status"><i class="mdi mdi-text-short pb-ico" aria-hidden="true"></i>
          <span>This quote is too long for this format. <router-link :to="'/inbox/' + pickedId">Shorten it with a "…" excerpt</router-link> or choose a larger format.</span></div>
        <div class="pb-actions gv-actions">
          <button type="button" class="pb-button lg" :disabled="!fits || rendering" @click="download"><i class="mdi mdi-download pb-ico" aria-hidden="true"></i>Download PNG</button>
          <span v-if="marked" class="pb-muted">Free graphics carry a small "Made with TrustClap" mark. <button type="button" class="pb-button link" @click="upsell">Remove it with {{ planName(graphicsPlan) }}</button></span>
        </div>
      </section>
    </div>
    <UpgradeDialog v-model="upgradeOpen" :detail="upgrade" />
  </div>
</template>

<script>
import EmptyState from '../components/EmptyState.vue';
import PageHeader from '../components/PageHeader.vue';
import PlanChip from '../components/PlanChip.vue';
import SkeletonList from '../components/SkeletonList.vue';
import UpgradeDialog from '../components/UpgradeDialog.vue';
import workspace from '../mixins/workspace';
import { get, message } from '../api';
import { FORMATS, TEMPLATES, canvasBlob, renderQuoteCard } from '../lib/quoteCard';

export default {
  name: 'GraphicsView',
  components: { EmptyState, PageHeader, PlanChip, SkeletonList, UpgradeDialog },
  mixins: [workspace],
  data: () => ({ items: [], pickedId: null, format: 'square', template: 'paper', showName: true, showRole: true, showRating: true,
    loading: true, error: '', fits: true, rendering: false, upgrade: null, upgradeOpen: false, formats: FORMATS, templates: TEMPLATES }),
  computed: {
    advanced() { return this.limits.graphics === 'advanced'; },
    marked() { return !this.advanced; },
    graphicsPlan() { return this.availableOn('graphics') || ''; },
    picked() { return this.items.find(item => item.id === this.pickedId) || null; },
    role() { return this.picked ? [this.picked.job_title, this.picked.company].filter(Boolean).join(', ') : ''; },
    brandColor() { return (this.brand && this.brand.color) || '#B4472B'; },
    renderKey() { return [this.pickedId, this.format, this.template, this.showName, this.showRole, this.showRating, this.brandColor].join('|'); }
  },
  watch: {
    brandId() { this.load(); },
    renderKey() { this.$nextTick(this.render); }
  },
  async mounted() {
    if (!(await this.requireWorkspace())) return;
    this.load();
  },
  methods: {
    label(item) {
      const text = item.text.length > 70 ? item.text.slice(0, 69) + '…' : item.text;
      return (item.name || item.byline) + ': ' + text;
    },
    locked(kind, key) { return !this.advanced && (kind === 'format' ? key !== 'square' : key !== 'paper'); },
    upsell() { this.upgrade = { feature: 'graphics' }; this.upgradeOpen = true; },
    choose(kind, key) {
      if (this.locked(kind, key)) { this.upsell(); return; }
      this[kind] = key;
    },
    async load() {
      if (!this.brandId) return;
      this.loading = true;
      this.error = '';
      try {
        const wall = await get('/brands/' + this.brandId + '/wall');
        this.items = wall.published || [];
        if (!this.picked) this.pickedId = this.items.length ? this.items[0].id : null;
      } catch (error) { this.error = message(error); } finally {
        this.loading = false;
        this.$nextTick(this.render);
      }
    },
    async render() {
      const canvas = this.$refs.canvas;
      if (!canvas || !this.picked) return;
      this.rendering = true;
      const item = this.picked;
      const result = await renderQuoteCard(canvas, {
        format: this.format, template: this.template, brandName: this.brand ? this.brand.name : '', brandColor: this.brandColor, marked: this.marked,
        card: { text: item.text, name: this.showName ? item.name : null, role: this.showRole ? this.role : '', rating: this.showRating ? item.rating : null,
          source: item.source || '' }
      });
      this.fits = result.fits;
      this.rendering = false;
    },
    async download() {
      const blob = await canvasBlob(this.$refs.canvas);
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'trustclap-quote-' + this.format + '.png';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 5000);
    }
  }
};
</script>

<style scoped>
.gv-grid{display:grid;grid-template-columns:minmax(0,.85fr) minmax(0,1.15fr);gap:24px;align-items:start}
.gv-controls{display:grid;gap:16px}
.gv-h{margin-bottom:14px}
.gv-gap{margin-top:8px!important}
.gv-source{margin:12px 0 0}
.gv-options{display:grid;grid-template-columns:repeat(auto-fill,minmax(min(140px,100%),1fr));gap:10px}
.gv-option{display:flex;flex-direction:column;align-items:stretch;gap:6px;text-align:left;padding:12px;border:1.5px solid #E6DED2;border-radius:12px;background:#fff;cursor:pointer;min-height:44px}
.gv-option:hover{border-color:#D4C9B8}
.gv-option.picked{border-color:#B4472B;box-shadow:0 0 0 3px rgba(180,71,43,.15)}
.gv-option.locked{background:#FBF8F3}
.gv-o-name{display:flex;flex-wrap:wrap;justify-content:space-between;align-items:center;gap:4px;font:700 14px var(--font-body);color:#1F2328}
.gv-shape{display:block;background:#F3EEE6;border:1px solid #D4C9B8;border-radius:4px;height:40px}
.gv-shape.square{width:40px}
.gv-shape.portrait{width:32px}
.gv-shape.website{width:76px}
.gv-shape.card{width:60px}
.gv-swatch{display:block;height:40px;border-radius:6px;border:1px solid #D4C9B8}
.gv-swatch.paper{background:#FAF7F2;box-shadow:inset 0 0 0 6px #fff}
.gv-swatch.minimal{background:linear-gradient(#fff,#fff) padding-box,#fff;border-top:4px solid #B4472B}
.gv-preview{position:sticky;top:84px}
.gv-canvas-wrap{border:1px solid #E6DED2;border-radius:14px;background:repeating-conic-gradient(#F3EEE6 0% 25%,#FAF7F2 0% 50%) 50% / 24px 24px;padding:18px;display:grid;place-items:center}
.gv-canvas{max-width:100%;max-height:640px;width:auto;height:auto;border-radius:6px;box-shadow:0 10px 30px rgba(31,35,40,.14)}
.gv-actions{margin-top:16px}
@media(max-width:1000px){.gv-grid{grid-template-columns:minmax(0,1fr)}.gv-preview{position:static;order:-1}}
</style>
